import React, {useEffect, useState, ChangeEvent} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import {client, clientNoJson} from '../../dataProvider';
import {AppBar, Checkbox, FormControl, InputLabel, MenuItem, Select, Tab, withStyles} from "@material-ui/core";
import {getFileNameFromContentDisposition, initField} from "../../shared/utils";
import {TabContext, TabList, TabPanel} from "@material-ui/lab";
import {RowSummary} from "./Row";
import {AutocompleteArrayInput, AutocompleteInput, ReferenceArrayInput, ReferenceInput} from "react-admin";
import {ReferenceInputSubject} from "../../components/reference-inputs/ReferenceInputSubject";
import {
    ClientAutocomplete,
    ProvinceAutocomplete, ReceiverAutocomplete,
    RegionAutocomplete,
    WarehouseAutocomplete
} from "./FilterAutocomplete";

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
    },
    pageTitle: {
        marginBottom: theme.spacing(3),
        fontWeight: 600,
        color: theme.palette.primary.main,
    },
    filters: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: '#fff',
        borderRadius: theme.spacing(1),
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    },
    filterItem: {
        width: '100%',
    },
    tableHeadCell: {
        fontWeight: 600,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    tableContainer: {
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    nestedTable: {
        backgroundColor: '#ffffff',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        overflow: 'hidden',
    },
    fileRow: {
        backgroundColor: '#f0f4f8',
        '&:hover': {
            backgroundColor: '#e8eef5',
        },
    },
    fileCell: {
        paddingLeft: theme.spacing(6),
    },
    fileIcon: {
        verticalAlign: 'middle',
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
        fontSize: '1.2rem',
    },
    totalsChip: {
        fontWeight: 600,
        fontSize: '0.875rem',
    },
    emptyState: {
        padding: theme.spacing(4),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    sortIcon: {
        verticalAlign: 'middle',
        fontSize: '1rem',
        marginLeft: theme.spacing(0.5),
    },
}));

const StyledTableCell = withStyles((theme: any) => ({
    head: {
        color: theme.palette.common.black,
        fontWeight: 600,
        fontSize: '0.95rem',
        opacity: "90%"
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

function getDateFilter(from: boolean) {
    const today = new Date();
    let hours;
    if (today.getHours() >= 12) {
        if(from){
            hours = "15:00";
        } else {
            hours = "18:00";
            today.setDate(today.getDate() + 1);
        }

    } else {
        hours = from ? "08:00" : "15:00";
    }
    return `${today.toISOString().split('T')[0]}T${hours}`
}

const PageSummaryOfTheDay: React.FC = () => {
    const classes = useStyles();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState<{
        date_from: string;
        date_to: string;
        threshold_weight: string;
        same_day: string;
        threshold_package_number: string;
        province: string | null;
        region: string | null;
        warehouse: string | null;
        client: string | null,
        receiver: string | null,
        delivery_type: string[],
    }>({
        date_from: `${getDateFilter(true)}`,
        date_to: `${getDateFilter(false)}`,
        threshold_weight: '150',
        same_day: 'A',
        threshold_package_number: '20',
        province: null,
        region: null,
        warehouse: null,
        client: null,
        receiver: null,
        delivery_type: ["NO"],
    });
    const [value, setValue] = useState("1");
    const [orderBy, setOrderBy] = useState('totP');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [selectedRow, setSelectedRow] = useState([]);

    const buildQueryParams = () => {
        const params: any = {};

        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
        if (filters.threshold_weight) params.threshold_weight = filters.threshold_weight;
        if (filters.threshold_package_number) params.threshold_package_number = filters.threshold_package_number;
        if (filters.province) params.province = filters.province;
        if (filters.region) params.region = filters.region;
        if (filters.warehouse) params.warehouse = filters.warehouse;
        if(filters.client) params.client = filters.client;
        if(filters.receiver) params.receiver = filters.receiver;

        params.delivery_type = filters.delivery_type.join(",");
        params.same_day = filters.same_day;
        params.view = value === "1" ? "receiver" : "client";
        params.order_by = orderBy;
        params.order = order;

        return params;
    };

    const loadSummary = () => {
        setLoading(true);
        const params = buildQueryParams();

        client('summary_of_the_day', {
            method: 'GET',
            params,
        })
            .then((response) => {
                setRows(response);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadSummary();
        setSelectedRow([]);
    }, [filters, value, orderBy, order]);

    const handleTextFilterChange = (name: keyof typeof filters) => (event: ChangeEvent<HTMLInputElement>) => {
                setFilters((prev) => ({
                    ...prev,
                    [name]: event.target.value,
                }));
            };

    const exportTable = async () => {
        setLoading(true);
        const params = buildQueryParams();
        let res = null;
        try {
            res = await clientNoJson(`/api/export_summary_of_the_day?${new URLSearchParams(params).toString()}`, {
                method: "GET",
                responseType: "arraybuffer",
            });
        } catch (err) {
            setLoading(false);
            return;
        }
        const data = res.data;
        const url = window.URL.createObjectURL(
            new Blob([data], {
                type: res.headers["content-type"],
            })
        );
        const actualFileName = getFileNameFromContentDisposition(
            res.headers["content-disposition"]
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", actualFileName);
        document.body.appendChild(link);
        link.click();
        if (link.parentNode) link.parentNode.removeChild(link);
        setLoading(false);
    }

    const handleChange = (event: any, newValue: any) => {
        setValue(newValue);
    };

    const handleSort = (column: string) => {
        if (orderBy === column) {
            setOrder(order === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(column);
            setOrder('desc');
        }
    };

    const getSortIcon = (column: string) => {
        if (orderBy !== column) return null;
        return order === 'asc' ?
            <ArrowUpwardIcon className={classes.sortIcon}/> :
            <ArrowDownwardIcon className={classes.sortIcon}/>;
    };

    const printDetail = async () => {
        setLoading(true);
        let res = null;
        try {
            res = await clientNoJson(`/api/print_detail_of_summary/`, {
                method: "POST",
                data: selectedRow,
                responseType: "arraybuffer",
            });
        } catch (err) {
            setLoading(false);
            return;
        }
        const data = res.data;
        const url = window.URL.createObjectURL(
            new Blob([data], {
                type: res.headers["content-type"],
            })
        );
        const actualFileName = getFileNameFromContentDisposition(
            res.headers["content-disposition"]
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", actualFileName);
        document.body.appendChild(link);
        link.click();
        if (link.parentNode) link.parentNode.removeChild(link);
        setLoading(false);
    }

    const mainLabel = value === "1" ? "Destinatario" : "Cliente";


    return (
        <Box className={classes.root}>
            <Box className={classes.filters}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h4" className={classes.pageTitle}>
                            Riassunto Giornaliero
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Data da"
                            type="datetime-local"
                            variant="outlined"
                            size="small"
                            className={classes.filterItem}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={filters.date_from}
                            onChange={handleTextFilterChange('date_from')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Data a"
                            type="datetime-local"
                            variant="outlined"
                            size="small"
                            className={classes.filterItem}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={filters.date_to}
                            onChange={handleTextFilterChange('date_to')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Peso minimo (kg)"
                            type="number"
                            variant="outlined"
                            size="small"
                            className={classes.filterItem}
                            value={filters.threshold_weight}
                            onChange={handleTextFilterChange('threshold_weight')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Colli minimi"
                            type="number"
                            variant="outlined"
                            size="small"
                            className={classes.filterItem}
                            value={filters.threshold_package_number}
                            onChange={handleTextFilterChange('threshold_package_number')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={2}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel id="same_day">Same day</InputLabel>
                            <Select
                                labelId="same_day"
                                value={filters.same_day}
                                variant="outlined"
                                onChange={(event) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        same_day: event.target.value as string,
                                    }))
                                }
                                label="Same day"
                            >
                                <MenuItem value="A">Misto</MenuItem>
                                <MenuItem value="Y">Si</MenuItem>
                                <MenuItem value="N">No</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} md={2}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel id="delivery_type">Tipo</InputLabel>
                            <Select
                                labelId="delivery_type"
                                value={filters.delivery_type}
                                variant="outlined"
                                onChange={(event) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        delivery_type: event.target.value as string[],
                                    }))
                                }
                                label="Tipo"
                                multiple
                            >
                                <MenuItem value="NO">Consegna</MenuItem>
                                <MenuItem value="RC">Riconsegna</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} md={2}>
                        <ProvinceAutocomplete
                            value={filters.province}
                            onChange={(value:any) => setFilters(prev => ({ ...prev, province: value }))}
                            className={classes.filterItem}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={2}>
                        <RegionAutocomplete
                            value={filters.region}
                            onChange={(value:any) => setFilters(prev => ({ ...prev, region: value }))}
                            className={classes.filterItem}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={2}>
                        <WarehouseAutocomplete
                            value={filters.warehouse}
                            onChange={(value:any) => setFilters(prev => ({ ...prev, warehouse: value }))}
                            className={classes.filterItem}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={2}>
                        <ClientAutocomplete value={filters.client} onChange={(value:any) => setFilters(prev => ({ ...prev, client: value }))}/>
                    </Grid>
                    <Grid item xs={12} sm={12} md={2}>
                        <ReceiverAutocomplete value={filters.receiver} onChange={(value:any) => setFilters(prev => ({ ...prev, receiver: value }))}/>
                    </Grid>

                    <Grid item xs={12} style={{justifyContent: 'space-between', display: 'flex'}}>
                        {value === "1" && <Button
                            variant="contained"
                            color="primary"
                            disabled={selectedRow.length === 0}
                            onClick={printDetail}
                        >
                            Stampa Dettaglio
                        </Button>}
                        <Box display="flex" justifyContent="flex-end" flexGrow={2} style={{gap: 16}}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={exportTable}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} color="inherit"/> : null}
                            >
                                {loading ? 'Caricamento...' : 'Esporta'}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <TabContext value={value}>
                <AppBar position="static">
                    <TabList onChange={handleChange} aria-label="simple tabs example">
                        <Tab label="Vista Destinatario" value="1"/>
                        <Tab label="Vista Cliente" value="2"/>
                    </TabList>
                </AppBar>
                <TabPanel value="1">
                    <TableContainer component={Paper} className={classes.tableContainer}>
                        <Table aria-label="summary table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell/>
                                    <StyledTableCell>
                                        <Checkbox
                                            checked={selectedRow === rows}
                                            onChange={(event, checked) => checked ? setSelectedRow(rows) : setSelectedRow([])}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell
                                        className={classes.tableHeadCell}
                                        onClick={() => handleSort('business_name')}
                                    >
                                        {mainLabel}
                                        {getSortIcon('business_name')}
                                    </StyledTableCell>
                                    <StyledTableCell className={classes.tableHeadCell}>
                                        Località
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="right"
                                        className={classes.tableHeadCell}
                                        onClick={() => handleSort('totD')}
                                    >
                                        Tot. Missioni
                                        {getSortIcon('totD')}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="right"
                                        className={classes.tableHeadCell}
                                        onClick={() => handleSort('totP')}
                                    >
                                        Tot. Colli
                                        {getSortIcon('totP')}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="right"
                                        className={classes.tableHeadCell}
                                        onClick={() => handleSort('totW')}
                                    >
                                        Tot. Peso
                                        {getSortIcon('totW')}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="right"
                                        className={classes.tableHeadCell}
                                        onClick={() => handleSort('totV')}
                                    >
                                        Tot. Volume
                                        {getSortIcon('totV')}
                                    </StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className={classes.emptyState}>
                                            <CircularProgress/>
                                            <Typography variant="body2" style={{marginTop: 16}}>
                                                Caricamento dati...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className={classes.emptyState}>
                                            <Typography variant="body1" color="textSecondary">
                                                Nessun dato trovato per i filtri selezionati.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    Object.entries(rows).map(([key, value]) => (
                                        <RowSummary key={key} row={value} viewType="receiver" classes={classes}
                                                    selectedRow={selectedRow} setSelectedRow={setSelectedRow}/>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>
                <TabPanel value="2">
                    <TableContainer component={Paper} className={classes.tableContainer}>
                        <Table aria-label="summary table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell/>
                                    <StyledTableCell
                                        className={classes.tableHeadCell}
                                        onClick={() => handleSort('business_name')}
                                    >
                                        {mainLabel}
                                        {getSortIcon('business_name')}
                                    </StyledTableCell>
                                    <StyledTableCell className={classes.tableHeadCell}>
                                        Località
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="right"
                                        className={classes.tableHeadCell}
                                        onClick={() => handleSort('totD')}
                                    >
                                        Tot. Missioni
                                        {getSortIcon('totD')}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="right"
                                        className={classes.tableHeadCell}
                                        onClick={() => handleSort('totP')}
                                    >
                                        Tot. Colli
                                        {getSortIcon('totP')}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="right"
                                        className={classes.tableHeadCell}
                                        onClick={() => handleSort('totW')}
                                    >
                                        Tot. Peso
                                        {getSortIcon('totW')}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="right"
                                        className={classes.tableHeadCell}
                                        onClick={() => handleSort('totV')}
                                    >
                                        Tot. Volume
                                        {getSortIcon('totV')}
                                    </StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className={classes.emptyState}>
                                            <CircularProgress/>
                                            <Typography variant="body2" style={{marginTop: 16}}>
                                                Caricamento dati...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className={classes.emptyState}>
                                            <Typography variant="body1" color="textSecondary">
                                                Nessun dato trovato per i filtri selezionati.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    Object.entries(rows).map(([key, value]) => (
                                        <RowSummary key={key} row={value} viewType="client" classes={classes}/>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>
            </TabContext>
        </Box>
    );
};
export default PageSummaryOfTheDay